import { Popup } from '@progress/kendo-react-popup';
import ClickOutside from 'app/widgets/ClickOutside';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { ActionsListItem } from './types';
import './Menu.less';

interface Props {
  anchorEl?: HTMLElement;
  actions: ActionsListItem[];
  onClose: () => void;
  menuClassName?: string;
}

export default function Menu({
  anchorEl,
  actions,
  onClose,
  menuClassName,
}: Props) {
  const menuOpen = Boolean(anchorEl);

  const activeActions = useMemo(
    () => actions.filter(({ exclude }) => !exclude),
    [actions],
  );

  useEffect(() => {
    if (menuOpen) {
      // NOTE: Kendo Popup can occasionally leave behind a "ghost" popup container
      // when it is rapidly closed on scroll + reopened. We keep the scroll handler
      // as a stable function reference per effect run.
      const handleScrollClose = () => onClose();
      window.addEventListener('scroll', handleScrollClose, true);

      return () => {
        window.removeEventListener('scroll', handleScrollClose, true);
      };
    }

    return;
  }, [menuOpen, onClose]);

  return (
    <Popup
      // Note: React doesn't always render this popup correctly when we close it onScroll.
      // The key prop forces it to be unmounted/mounted and prevents "ghost" popup DOM nodes.
      key={menuOpen ? 'open' : 'closed'}
      anchor={anchorEl}
      show={menuOpen}
      popupAlign={{ horizontal: 'right', vertical: 'top' }}
    >
      <ClickOutside
        onClickOutside={event => {
          if (
            anchorEl &&
            (event.target === anchorEl ||
              anchorEl.contains(event.target as Node))
          ) {
            return;
          }

          onClose();
        }}
      >
        <ul className={clsx('base-menu__list', menuClassName)}>
          {activeActions.map(({ label, action, disabled }, index) => (
            <li
              key={`${label}-${index}`}
              className={clsx('base-menu__item', {
                'base-menu__item_disabled': disabled,
              })}
              onClick={event => {
                event.stopPropagation();

                if (!disabled) {
                  action();
                  onClose();
                }
              }}
            >
              {label}
            </li>
          ))}
        </ul>
      </ClickOutside>
    </Popup>
  );
}
